import React, { createContext, useContext, useMemo } from 'react';
import i18next from 'i18next';
import { platformService } from '../../../shared/services/platform.service';
import { AppStateValue } from '../../../AppState';
import { MyWorkListViewType, STATUS_GUID } from '../../../shared/utils/constants';
import { InlinePickerCell, PickerField } from '../../../shared/components/table/InlinePickerCell';
import { InlineStatusCell } from '../../../shared/components/table/InlineStatusCell';
import { InlineRequesterCell } from '../../../shared/components/table/InlineRequesterCell';
import { TicketProps } from '../../../shared/interfaces/ticketproperties.interface';
import { ICustomProperty } from '../../../shared/interfaces/customproperties.interface';
import { InlineCustomPropertyCell, InlineDateCell, committing } from '../../../shared/components/table/InlineFieldCells';
import { currentPhaseFilter, isLifecycleRunning } from '../../../shared/utils/lifecyclePhaseFilter';
import { InlineComboCell } from '../../../shared/components/table/InlineComboCell';
import { AssignmentColumn, AssignmentDraft, InlineAssignmentCell, InlineEditRefused } from '../../../shared/components/table/InlineAssignmentCell';
import { buildCategoryOptions, optionName } from '../../../shared/components/table/inlineCategoryOptions';
import { InlineTagsCell, TagPills, TagRecord } from '../../../shared/components/table/InlineTagsCell';
import { ticketSaveGuard } from '../../../shared/components/table/ticketSaveGuard';

/** What My Work's editable cells need that a row does not carry. */
export interface MyWorkEditContextValue {
  currentState: AppStateValue;
  /** Analyst access and Ticket Tasks are both licensed. */
  tasksEditable: boolean;
  /** Analyst access is licensed. */
  ticketsEditable: boolean;
  useTeam: boolean;
  useTicketType: boolean;
  useTicketLifecycle: boolean;
  /** Resolution notes are licensed and switched on, so Resolved asks for one. */
  resolutionNotesOn: boolean;
  resolutionNoteConfig?: TikitConfiguration;
  /** Fetch the page on screen again, so a row that no longer belongs to this list leaves it. */
  refresh: () => Promise<void>;
  /** Put saved values into a row before the reload, in My Work's own field names (see the cells' toRow*). */
  patch: (row: { ItemType?: number; Id?: number }, fields: Record<string, unknown>) => void;
}

export const MyWorkEditContext = createContext<MyWorkEditContextValue | null>(null);

/** A task row as GetMyWork sends it, after useMyWorkListFunctions has put its users on it. */
export interface MyWorkTaskRow {
  Id?: number;
  ItemType?: number;
  TicketId: number;
  Title?: string;
  StatusId?: number | null;
  StatusName?: string;
  TeamId?: number | null;
  SupportGroupId?: number | null;
  AssigneeUsers?: ({ Id: number } | undefined)[];
  Collaborators?: number[];
  LifecyclePhaseName?: string;
  LifecycleStatusId?: string;
  /** The row's ticket is closed - for a task, the ticket it belongs to (GetMyWork). */
  TicketClosed?: boolean;
  /** The row's ticket was merged into another (GetMyWork). */
  TicketMerged?: boolean;
}

/** A ticket row as GetMyWork sends it, after useMyWorkListFunctions and DetailsListTable have dressed it. */
export interface MyWorkTicketRow extends MyWorkTaskRow {
  TypeId?: number | null;
  CategoryId?: number | null;
  PriorityId?: number | null;
  RequesterId?: number | null;
  /** Requester and affected users together, as the Requester column draws them. */
  Requester?: { Id: number; FullName?: string; UserName?: string; AadObjectId?: string }[];
  AffectedUsers?: number[];
  StatusRecord?: TicketProps;
  TicketType?: string;
  Category?: string;
  Priority?: string;
}

/**
 * Row identity for a cell. My Work's rows are keyed by position and mix tickets, tasks and approvals, whose ids
 * overlap - ticket 12 and task 12 - so a cell told only the id could take one for the other after a reload.
 */
const rowKey = (row: MyWorkTaskRow) => (row.ItemType ?? 0) * 1_000_000_000 + (row.Id ?? 0);

/**
 * A task is edited from My Work as the task dialog edits it: only by a licensed analyst, and - for a task on a
 * lifecycle phase - only while that lifecycle runs (My Work already lists only current-phase tasks).
 */
export const isTaskEditable = (row: MyWorkTaskRow, context: MyWorkEditContextValue | null) =>
  !!context?.tasksEditable && row.ItemType === MyWorkListViewType.Tasks && !row.TicketClosed && !row.TicketMerged &&
  (!row.LifecyclePhaseName || isLifecycleRunning(row.LifecycleStatusId));

const assigneeId = (row: MyWorkTaskRow) => row.AssigneeUsers?.[0]?.Id ?? null;

type Person = { Id?: number } | null | undefined;

/**
 * The assignment panel's patch, in the ticket list's field names, as My Work keeps them: the group's name is
 * SupportGroupName, Assignee is the id with the person in AssigneeUsers, collaborators are ids in Collaborators
 * with the people in TicketCollaborators.
 */
const toRowRouting = (fields: Record<string, unknown>) => {
  const out: Record<string, unknown> = {};
  if ('TeamId' in fields) {
    const assignee = fields.Assignee as Person;
    Object.assign(out, {
      TeamId: fields.TeamId, TeamName: fields.TeamName,
      SupportGroupId: fields.SupportGroupId, SupportGroupName: fields.GroupName,
      Assignee: fields.AssigneeId, AssigneeUsers: assignee ? [assignee] : []
    });
  }
  if ('TicketCollaborators' in fields) {
    const pairs = fields.TicketCollaborators as { PlatformUserId: number; PlatformUser: Person }[];
    Object.assign(out, { Collaborators: pairs.map(p => p.PlatformUserId), TicketCollaborators: pairs.map(p => p.PlatformUser) });
  }
  return out;
};

/** The requester panel's patch as My Work keeps it: the affected users as ids plus AffectedPlatformUsers. */
const toRowPeople = (fields: { RequesterId?: number; Requester?: Person; AffectedUsers?: { PlatformUserId: number; PlatformUser?: Person }[] }) => ({
  ...('RequesterId' in fields ? { RequesterId: fields.RequesterId, Requester: fields.Requester } : {}),
  ...(fields.AffectedUsers ? { AffectedUsers: fields.AffectedUsers.map(a => a.PlatformUserId), AffectedPlatformUsers: fields.AffectedUsers.map(a => a.PlatformUser) } : {})
});

/**
 * Asked again just before a task or approval on a lifecycle phase is saved, as the task dialog and the lifecycle tab
 * do: the row stays on screen after its phase has moved on or its lifecycle has ended. Asked with the filter My Work
 * lists them by, so "still editable" means exactly "would still be listed".
 */
export const refuseUnlessPhaseActive = async (row: { Id?: number; ItemType?: number; LifecyclePhaseName?: string }) => {
  if (!row.LifecyclePhaseName) return;
  const api = new platformService();
  const isTask = row.ItemType === MyWorkListViewType.Tasks;
  const still: unknown[] = isTask
    ? await api.getTicketTasks(`?$select=Id&$filter=Id eq ${row.Id} and ${currentPhaseFilter('', true)}`)
    : await api.getApprovalsByEmail(`Id eq ${row.Id} and ${currentPhaseFilter('Approval/', true)}`);
  if (!still.length) throw new InlineEditRefused(i18next.t(isTask
    ? 'ticket-details.edit-ticket-form.task.error.phase-not-active'
    : 'ticket-details.edit-ticket-form.approval.error.phase-not-active'));
};

/**
 * Saves a task as the task dialog does - the whole set in one PATCH, collaborators through their own action -
 * after asking whether its ticket has closed, which the row does not say and the server does not check.
 */
const saveTask = async (row: MyWorkTaskRow, changes: { StatusId?: number; TeamId?: number | null; SupportGroupId?: number | null; AssigneeId?: number | null }, collaboratorIds?: number[]) => {
  const api = new platformService();
  const { data } = await api.getTicket(row.TicketId, '?$select=Closed,IsMerged');
  if (data?.Closed || data?.IsMerged) throw new InlineEditRefused(i18next.t('ticket.ticket-board.closed-ticket-cannot-update'));
  await refuseUnlessPhaseActive(row);
  await api.updateTicketTask(`/TicketTask/${row.Id}`, {
    Title: row.Title,
    TicketId: row.TicketId,
    StatusId: row.StatusId,
    AssigneeId: assigneeId(row),
    TeamId: row.TeamId ?? null,
    SupportGroupId: row.SupportGroupId ?? null,
    ...changes
  });
  if (collaboratorIds) await api.updateTicketTaskCollaborators(row.Id, collaboratorIds);
};

/**
 * The ticket page's rules (EditTicketForm): a merged or deflected ticket cannot be saved at all, and a closed one
 * only by changing its status, which reopens it. The save's guard asks the server again, for a row gone stale.
 */
const isStatusEditableRow = (row: MyWorkTicketRow, context: MyWorkEditContextValue | null) =>
  !!context?.ticketsEditable && row.ItemType === MyWorkListViewType.Tickets && !row.TicketMerged &&
  row.StatusRecord?.Guid !== STATUS_GUID.DEFLECTED;

const isTicketRow = (row: MyWorkTicketRow, context: MyWorkEditContextValue | null) =>
  isStatusEditableRow(row, context) && !row.TicketClosed;

/** Type, Category or Priority of a ticket row on My Work - the ticket list's cell. */
export const TicketPickerCell = ({ row, field, resting }: { row: MyWorkTicketRow; field: PickerField; resting: React.ReactElement }) => {
  const context = useContext(MyWorkEditContext);
  if (!isTicketRow(row, context) || (field === 'TicketTypeId' && !context.useTicketType)) return resting;
  const { currentState } = context;
  const setup: Record<PickerField, [string, number | null | undefined, string | undefined, TicketProps[]]> = {
    TicketTypeId: [i18next.t('ticket.ticket-list.type'), row.TypeId, row.TicketType, currentState.ticketTypes],
    CategoryId: [i18next.t('ticket.ticket-list.category'), row.CategoryId, row.Category, currentState.categories],
    PriorityId: [i18next.t('ticket.ticket-list.priority'), row.PriorityId, row.Priority, currentState.priority]
  };
  const [label, valueId, displayName, records] = setup[field];
  return (
    <InlinePickerCell
      ticketId={row.Id}
      field={field}
      label={label}
      valueId={valueId ?? null}
      displayName={displayName ?? '-'}
      records={records}
      onSaved={context.refresh}
      guard={ticketSaveGuard(row.Id, false, context.currentState.customProperties)}
    />
  );
};

/** A ticket row's status on My Work - the ticket list's cell, with its lifecycle, tasks and resolution rules. */
export const TicketStatusCell = ({ row, renderPill, resting }: { row: MyWorkTicketRow; renderPill: (statusId: number | null) => React.ReactNode; resting: React.ReactElement }) => {
  const context = useContext(MyWorkEditContext);
  if (!isStatusEditableRow(row, context)) return resting;
  const { currentState } = context;
  return (
    <InlineStatusCell
      ticket={{ Id: row.Id, Title: row.Title, StatusId: row.StatusId, TicketLifecycle: { Status: { Guid: row.LifecycleStatusId } } }}
      statuses={currentState.ticketStatus}
      taskStatuses={currentState.taskStatus}
      lifecycleEnabled={context.useTicketLifecycle}
      resolutionNotesOn={context.resolutionNotesOn && !!context.resolutionNoteConfig}
      resolutionNoteConfig={context.resolutionNoteConfig}
      renderPill={renderPill}
      onSaved={context.refresh}
      guard={ticketSaveGuard(row.Id, true, context.currentState.customProperties)}
    />
  );
};

/** A ticket row's Requester and Affected Users on My Work - the ticket list's panel. */
export const TicketRequesterCell = ({ row, resting }: { row: MyWorkTicketRow; resting: React.ReactElement }) => {
  const context = useContext(MyWorkEditContext);
  if (!isTicketRow(row, context)) return resting;
  const people = row.Requester ?? [];
  const requester = people.find(user => user.Id === row.RequesterId);
  const affected = (row.AffectedUsers ?? []).map(id => ({ PlatformUserId: id, PlatformUser: people.find(user => user.Id === id) }));
  return (
    <InlineRequesterCell
      ticketId={row.Id}
      requester={requester}
      affectedUsers={affected}
      resting={resting}
      onSaved={context.refresh}
      onPatch={fields => context.patch(row, toRowPeople(fields))}
      guard={ticketSaveGuard(row.Id, false, context.currentState.customProperties)}
    />
  );
};

/** A ticket row's Team, Group, Assignee and Collaborators on My Work - the ticket list's panel. */
export const TicketAssignmentCell = ({ row, resting, column }: { row: MyWorkTicketRow; resting: React.ReactElement; column: AssignmentColumn }) => {
  const context = useContext(MyWorkEditContext);
  if (!isTicketRow(row, context)) return resting;
  const { currentState } = context;
  const guard = ticketSaveGuard(row.Id, false, context.currentState.customProperties);
  const save = async (draft: AssignmentDraft, changed: { routing: boolean; collaborators: boolean }) => {
    await guard();
    const api = new platformService();
    if (changed.routing) await api.updateTicket(row.Id, { TeamId: draft.teamId, SupportGroupId: draft.groupId, AssigneeId: draft.assigneeId });
    if (changed.collaborators) await api.UpdateCollaborators(row.Id, draft.collaboratorIds);
  };
  return (
    <InlineAssignmentCell
      ticket={{
        Id: row.Id,
        TeamId: row.TeamId ?? null,
        SupportGroupId: row.SupportGroupId ?? null,
        AssigneeId: assigneeId(row),
        TicketCollaborators: (row.Collaborators ?? []).map(id => ({ PlatformUserId: id }))
      }}
      resting={resting}
      column={column}
      teams={currentState.teams}
      groups={currentState.supportgroups}
      usersInRole={currentState.usersInRole}
      useTeam={context.useTeam}
      onSaved={context.refresh}
      onPatch={fields => context.patch(row, toRowRouting(fields))}
      save={save}
    />
  );
};

/** A ticket row's due or resolution date on My Work - the ticket list's calendar. */
export const TicketDateCell = ({ row, field, value, resting, restingFor }: {
  row: MyWorkTicketRow; field: 'DueDate' | 'ResolutionDate'; value?: string | null; resting: React.ReactElement; restingFor?: (date: Date | null) => React.ReactElement;
}) => {
  const context = useContext(MyWorkEditContext);
  if (!isTicketRow(row, context)) return resting;
  return (
    <InlineDateCell
      rowKey={row.Id}
      label={i18next.t(field === 'DueDate' ? 'ticket.ticket-list.due-date' : 'ticket.ticket-list.resolution-date')}
      value={value ? new Date(value) : null}
      clearable
      resting={resting}
      restingFor={restingFor}
      commit={committing<Date | null>(
        date => new platformService().updateTicket(row.Id, { [field]: date ? date.toISOString() : null })
          .then(() => context.patch(row, { [field]: date ? date.toISOString() : null })),
        context.refresh,
        ticketSaveGuard(row.Id, false, context.currentState.customProperties)
      )}
    />
  );
};

/** A ticket row's tags on My Work - the ticket list's panel; GetMyWork sends them as [{ Id, Name }]. */
export const TicketTagsCell = ({ row }: { row: MyWorkTicketRow & { Tags?: TagRecord[] } }) => {
  const context = useContext(MyWorkEditContext);
  const resting = <TagPills tags={row.Tags} />;
  if (!isTicketRow(row, context)) return row.ItemType === MyWorkListViewType.Tickets ? resting : <span />;
  return (
    <InlineTagsCell
      ticketId={row.Id}
      tags={row.Tags ?? []}
      resting={resting}
      onSaved={context.refresh}
      onPatch={tags => context.patch(row, { Tags: tags })}
      guard={ticketSaveGuard(row.Id, false, context.currentState.customProperties)}
    />
  );
};

/** A ticket row's custom property on My Work - the ticket list's cell, by the property's type. */
export const TicketCustomPropertyCell = ({ row, property, answers, resting }: { row: MyWorkTicketRow; property: ICustomProperty; answers: Record<string, unknown>; resting: React.ReactElement }) => {
  const context = useContext(MyWorkEditContext);
  if (!isTicketRow(row, context)) return resting;
  return (
    <InlineCustomPropertyCell
      ticketId={row.Id}
      property={property}
      answers={answers}
      resting={resting}
      onSaved={context.refresh}
      onPatch={fields => context.patch(row, fields)}
      guard={ticketSaveGuard(row.Id, false, context.currentState.customProperties, property.PropertyId)}
    />
  );
};

/** A task's status on My Work, typable in place like the ticket list's cells. */
export const TaskStatusCell = ({ row, resting }: { row: MyWorkTaskRow; resting: React.ReactElement }) => {
  const context = useContext(MyWorkEditContext);
  const taskStatuses = context?.currentState.taskStatus ?? [];
  const options = useMemo(() => buildCategoryOptions(taskStatuses), [taskStatuses]);
  if (!isTaskEditable(row, context)) return resting;

  const nameOf = (id: number | null) => (id === row.StatusId ? row.StatusName ?? '' : optionName(options, id));
  const commit = committing<number>(statusId => saveTask(row, { StatusId: statusId }), context.refresh);

  return (
    <InlineComboCell
      ticketId={rowKey(row)}
      valueId={row.StatusId ?? null}
      options={options}
      label={i18next.t('ticket.ticket-list.status')}
      restingTitle={nameOf}
      renderResting={id => (
        <div className="w-full truncate">
          <span>{nameOf(id)}</span>
        </div>
      )}
      commit={commit}
    />
  );
};

/** A task's Team, Group, Assignee and Collaborators on My Work - the ticket list's panel, saved to the task. */
export const TaskAssignmentCell = ({ row, resting, column }: { row: MyWorkTaskRow; resting: React.ReactElement; column: AssignmentColumn }) => {
  const context = useContext(MyWorkEditContext);
  if (!isTaskEditable(row, context)) return resting;
  const { currentState } = context;

  const save = async (draft: AssignmentDraft, changed: { routing: boolean; collaborators: boolean }) => {
    await saveTask(
      row,
      changed.routing ? { TeamId: draft.teamId, SupportGroupId: draft.groupId, AssigneeId: draft.assigneeId } : {},
      changed.collaborators ? draft.collaboratorIds : undefined
    );
  };

  return (
    <InlineAssignmentCell
      ticket={{
        Id: rowKey(row),
        TeamId: row.TeamId ?? null,
        SupportGroupId: row.SupportGroupId ?? null,
        AssigneeId: assigneeId(row),
        TicketCollaborators: (row.Collaborators ?? []).map(id => ({ PlatformUserId: id }))
      }}
      resting={resting}
      column={column}
      teams={currentState.teams}
      groups={currentState.supportgroups}
      usersInRole={currentState.usersInRole}
      useTeam={context.useTeam}
      teamRequired={false}
      defaultGroupOnTeam={false}
      onSaved={context.refresh}
      onPatch={fields => context.patch(row, toRowRouting(fields))}
      save={save}
    />
  );
};
